import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { userAccountApi } from '../../../shared/services/userAccountApi'
import PlanCard from '../components/pricing/PlanCard'
import PlanComparisonTable from '../components/pricing/PlanComparisonTable'
import type { Plan } from '../components/pricing/types'

/**
 * 요금제.
 *
 * claude.ai/design 의 Pricing 구성을 옮겼다. 플랜 내용은 디자인에 적힌 값이
 * 아니라 /plans/public 응답을 쓴다. 가격·한도·기능은 운영 데이터이고,
 * 백엔드가 미구현 기능을 걸러 보낸다.
 */

type BillingCycle = 'monthly' | 'yearly'

export default function UserPricingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const publicPlans =
        (await userAccountApi.getPublicPlans()) as unknown as Plan[]
      setPlans(
        publicPlans
          .filter(plan => plan.isActive)
          .sort((a, b) => a.order - b.order)
      )
    } catch (loadError) {
      console.error('Error fetching plans:', loadError)
      setError(t('pricing.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void fetchPlans()
  }, [fetchPlans])

  const visiblePlans = plans.filter(plan => plan.billingCycle === billingCycle)

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-2xl font-bold text-red-600">
          {t('pricing.errorTitle')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">{error}</p>
        <button
          type="button"
          onClick={() => void fetchPlans()}
          className="rounded-xl bg-primary-600 px-5 py-2.5 font-semibold text-white transition hover:bg-primary-700"
        >
          {t('pricing.retry')}
        </button>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-900"
      style={{
        backgroundImage:
          'radial-gradient(50% 40% at 50% 0%, rgba(224,236,255,0.9) 0%, rgba(224,236,255,0) 100%)',
      }}
    >
      <div className="mx-auto flex max-w-[960px] flex-col gap-12 px-6 pb-20 pt-16">
        {/* 머리말 */}
        <div className="flex flex-col items-center gap-3.5 text-center">
          <h1 className="text-[clamp(2.25rem,5vw,3.25rem)] font-extrabold tracking-tight text-gray-900 dark:text-white">
            {t('pricing.upgradeTitle')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('pricing.subtitle')}
          </p>

          <div
            role="radiogroup"
            aria-label={t('pricing.upgradeTitle')}
            className="mt-2.5 flex gap-0.5 rounded-xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
          >
            {(['monthly', 'yearly'] as BillingCycle[]).map(cycle => (
              <button
                key={cycle}
                type="button"
                role="radio"
                aria-checked={billingCycle === cycle}
                onClick={() => setBillingCycle(cycle)}
                className={`flex items-center gap-2 rounded-[9px] px-4 py-2 text-sm font-semibold transition ${
                  billingCycle === cycle
                    ? 'bg-gray-900 text-white dark:bg-primary-600'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                }`}
              >
                {t(`pricing.${cycle}`)}
                {cycle === 'yearly' && (
                  <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[11px] font-bold text-green-700 dark:bg-green-900/60 dark:text-green-300">
                    {t('pricing.yearlyDiscount')}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* 플랜 카드 — 칸 수를 고정하지 않는다. 플랜이 둘뿐인데 네 칸으로
            잡으면 카드가 좁아져 글자가 끊겨 읽힌다. */}
        <div
          className="grid items-stretch gap-5"
          style={{
            gridTemplateColumns:
              'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          }}
        >
          {visiblePlans.map(plan => (
            <PlanCard
              key={plan._id}
              plan={plan}
              onSelect={() => navigate('/register')}
              twoColumnFeatures={visiblePlans.length <= 2}
            />
          ))}
        </div>

        <PlanComparisonTable plans={visiblePlans} />

        <Link
          to="/dashboard"
          className="flex items-center justify-center gap-1.5 text-[15px] font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('pricing.backToDashboard')}
        </Link>
      </div>
    </div>
  )
}
